import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormFields } from './FormFields';
import { Icon } from '@blueprintjs/core';
import './FormFields.css';
import classNames from 'classnames';

const defaultHelperText = <></>;

export function FormImageUploader({
    isLoading = false,
    headingText = "",
    helperText = defaultHelperText,
    dangerHelperText = "",
    height = 150,
    width = 150,
    value = null,
    onChange = null,
    backgroundColor = "#FFFFFF",
    accept = "image/png",
    invalidFileTypeText = "The selected file type is not supported."
}) {
    const [src, setSrc] = useState("");
    const [fileTypeError, setFileTypeError] = useState("");
    const fileInputRef = useRef();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const result = await getImageSrc(value);
            if (!cancelled) {
                setSrc(result);
            }
        })();
        return () => { cancelled = true; };
    }, [value]);

    function getImageSrc(input) {
        if (!input) {
            return Promise.resolve("");
        }
        if (typeof File !== "undefined" && input instanceof File) {
            return readFileContents(input);
        }
        if (typeof input === "string" && input.startsWith("data:")) {
            return Promise.resolve(input);
        }
        if (typeof input === "string" && (input.startsWith("https://") || input.startsWith("/"))) {
            return Promise.resolve(input);
        }
        if (typeof input === "string") {
            return Promise.resolve(`data:image/png;base64,${input}`);
        }
        return Promise.resolve("");
    }

    function readFileContents(file) {
        return new Promise((resolve, reject) => {
            var fileReader = new FileReader();
            if (file) {
                fileReader.readAsDataURL(file);
            } else {
                reject("No file provided");
            }
            fileReader.onload = function (event) {
                resolve(event.target.result);
            };
            fileReader.onerror = function (event) {
                reject(event);
            };
        });
    }

    function isFileTypeAccepted(file) {
        // The HTML accept attribute is only a hint for the native file dialog; a user can switch
        // the dialog to "All files" and pick any type. Validate the MIME type in JS so an
        // unsupported file is rejected before it reaches onChange and the backend.
        const acceptedTypes = accept.split(",").map((type) => type.trim());

        return acceptedTypes.some((acceptedType) => {
            if (acceptedType.endsWith("/*")) {
                const prefix = acceptedType.slice(0, acceptedType.indexOf("/*") + 1);
                return file.type.startsWith(prefix);
            }
            return file.type === acceptedType;
        });
    }

    async function handleFileChange(e) {
        const file = e.target.files[0];
        if (!file) {
            return;
        }

        if (!isFileTypeAccepted(file)) {
            setFileTypeError(invalidFileTypeText);
            return;
        }

        setFileTypeError("");
        if (onChange) {
            onChange(file);
        }
    }

    function handleIconClick() {
        fileInputRef.current.click();
    }

    const dangerText = fileTypeError !== "" ? fileTypeError : dangerHelperText;

    return (
        <FormFields
            isLoading={isLoading}
            headingText={headingText}
            helperText={helperText}
            dangerHelperText={dangerText}
        >
            <div className={classNames("form-image-uploader-container", { "bp6-skeleton": isLoading })}
                style={{ width: width || 150, height: height || 150, backgroundColor: backgroundColor }}
            >
                <img
                    src={src}
                    alt="Uploaded"
                    className="form-image-uploader-img"
                />
                <input
                    type="file"
                    accept={accept}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                />
                <button
                    className="form-image-uploader-overlay"
                    onClick={handleIconClick}
                >
                    <Icon icon={"camera"} size={32} color="#FFFFFF" />
                </button>
            </div>
        </FormFields>
    );
}

FormImageUploader.propTypes = {
    isLoading: PropTypes.bool,
    headingText: PropTypes.string,
    helperText: PropTypes.node,
    dangerHelperText: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
    value: PropTypes.oneOfType([
        PropTypes.string,
        typeof File !== "undefined" ? PropTypes.instanceOf(File) : () => null
    ]),
    onChange: PropTypes.func,
    backgroundColor: PropTypes.string,
    accept: PropTypes.string,
    invalidFileTypeText: PropTypes.string
};
